import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  type EntryChangeResult,
  type FieldValue,
  type HookedRecordResult,
  type IAlertFn,
  type IBlockFn,
  type IMappingStepUpdateData,
  type IReviewData,
  type IReviewEntriesUpdatedData,
  type ITdmModifier,
  type ImportLogs,
  MappingLayer,
  DataImporter as RawNuvoImporter,
  type Values,
} from '@ingestro/importer-react'
import * as Sentry from '@sentry/browser'
import { EnvironmentContext } from 'config/environment'
import {
  UniversalImporterValueObjectsImportTypeEnum,
  UniversalImporterImportProcessMetadataInput,
} from 'generated/resources/datatable.graphql'
import { NuvoImporterDocument, UniversalImporterProcessDocument } from 'generated/resources/graphql'

import { Authorized } from 'modules/core/components/Authorized'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import useDownloadTemplate from 'modules/employees/hooks/useDownloadTemplate'
import { i18nOverrides } from 'modules/universal_importer/components/NuvoImporter/locales'
import { getStyle } from 'modules/universal_importer/components/NuvoImporter/styles'
import {
  type EditionData,
  Metadata,
} from 'modules/universal_importer/components/NuvoImporter/types'
import {
  type ColumnCustomization,
  useColumnCustomizations,
} from 'modules/universal_importer/components/NuvoImporter/useColumnCustomizations'
import {
  type Column,
  useColumnsMapping,
} from 'modules/universal_importer/components/NuvoImporter/useColumnsMapping'
import { useEditionData } from 'modules/universal_importer/components/NuvoImporter/useEditionData'
import { useFileUpload } from 'modules/universal_importer/components/NuvoImporter/useFileUpload'
import { useGetMetadata } from 'modules/universal_importer/components/NuvoImporter/useGetMetadata'
import { useLanguage } from 'modules/universal_importer/components/NuvoImporter/useLanguage'
import { useNavigationHandling } from 'modules/universal_importer/components/NuvoImporter/useNavigationHandling'
import { useNuvoSession } from 'modules/universal_importer/components/NuvoImporter/useNuvoSession'
import { useSendImportResults } from 'modules/universal_importer/components/NuvoImporter/useSendingImportResults'
import {
  addMappingValidations,
  addMissingConditionalColumns,
  annotateChangedColumns,
  applyCustomizationsToRow,
  buildCompositeKey,
  buildDropdownMaps,
  computeCellAnnotation,
  convertEditionValue,
  hasKey,
  isFieldValue,
  isHookedRecordResult,
  normalizeDateToISO,
} from 'modules/universal_importer/lib/helpers'
import { transformationFunctions } from 'modules/universal_importer/lib/transformations'
import { validationFunctions } from 'modules/universal_importer/lib/validations'

type ProcessBy = 'rows' | 'columns'

const columnMappingLayers: MappingLayer[] = ['historic', 'exact', 'smart', 'fuzzy']
const optionMappingLayers: MappingLayer[] = ['exact', 'fuzzy']
const defaultThresholdMapping = 0.8

const downloadPath = (
  importer_type: UniversalImporterValueObjectsImportTypeEnum,
  metadataInput: UniversalImporterImportProcessMetadataInput[]
) => {
  const queryParams = new URLSearchParams()
  queryParams.append('metadata', JSON.stringify(metadataInput))
  return `/universal_importer/templates/${importer_type}?${queryParams.toString()}`
}

type Props = {
  importType: UniversalImporterValueObjectsImportTypeEnum
  headerSelection?: boolean
  companyId: string
  metadata?: Metadata
  locale?: string
  editionData?: EditionData
  customBackNavigation?: string
  customFinishNavigation?: string
  customLoading?: React.ReactNode
  isEdition?: boolean
  forceCreateAction?: boolean
  parentProcessId?: string | null
  rootProcessId?: string | null
  onLoading?: (loading: boolean) => void
  onUploadedFile?: (file: File) => void
  onDownloadTemplate?: () => void
  onCompleted?: () => void
  onError?: (error_message?: string) => void
  processBy?: ProcessBy
  thresholdMapping?: number
  /** In edition mode, show file upload and use uploaded file in Nuvo instead of preloaded DB data */
  useFileUploadInEdition?: boolean
  initialFile?: File
  /** When provided, only these field keys (plus identifiers) are shown in the importer */
  selectedFields?: string[]
}

const NuvoImporter = ({
  importType,
  headerSelection = false,
  companyId,
  metadata = {},
  locale = 'en-US',
  customBackNavigation,
  customFinishNavigation,
  customLoading,
  isEdition,
  forceCreateAction,
  parentProcessId,
  rootProcessId,
  onLoading,
  onUploadedFile,
  onDownloadTemplate,
  onCompleted,
  onError,
  processBy = 'rows',
  thresholdMapping = defaultThresholdMapping,
  useFileUploadInEdition,
  initialFile,
  selectedFields,
}: Props) => {
  const { nuvoApiKey, nuvoDeveloperMode } = React.useContext(EnvironmentContext)
  const navigate = useNavigate()

  // Controls what isEdition value is sent to backend.
  // Default: follows isEdition prop. Overrides (add new cases here):
  //  - forceCreateAction: sends false even when loading from DB
  //  - useFileUploadInEdition: sends true because data comes from file but targets existing records
  const effectiveIsEdition = (() => {
    if (forceCreateAction) return false
    if (useFileUploadInEdition) return true
    return !!isEdition
  })()

  const { sendImportResults } = useSendImportResults({ isEditionMode: effectiveIsEdition })
  const {
    access: currentUser,
    company: { subscription },
  } = useCurrentSession()
  const [hasChanges, setHasChanges] = React.useState(false)
  const nuvoImporterRef = React.useRef<HTMLDivElement>(null)
  const editionDataByIdentifierRef = React.useRef(new Map<string, EditionData[number]>())
  const fixProcessIds = metadata?.import_process_ids
  const hasFix = Array.isArray(fixProcessIds) && fixProcessIds.length > 0
  const editionOrFix = isEdition || hasFix

  const enrichedMetadata = React.useMemo(() => {
    if (hasFix) {
      return {
        ...metadata,
        is_fix: [true],
      }
    }
    return metadata
  }, [metadata, hasFix])
  const { metadataInput, legalEntitiesIds } = useGetMetadata(enrichedMetadata)
  const { data, loading } = useQuery(NuvoImporterDocument, {
    variables: { companyId, importType, metadata: metadataInput },
  })
  const { downloading, onDownload, error, value } = useDownloadTemplate({
    path: downloadPath(importType, metadataInput),
  })
  const [initialData, setInitialData] = React.useState<EditionData | undefined>(undefined)
  const [originalFile, setOriginalFile] = React.useState<File | undefined>(undefined)
  const [filteredRowCount, setFilteredRowCount] = React.useState<number | undefined>(undefined)
  const isDownloadingRef = React.useRef(false)
  const tracker = React.useMemo(
    () => ({
      id: crypto.randomUUID(),
      refresh() {
        this.id = crypto.randomUUID()
      },
    }),
    []
  )
  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser.id,
      company_id: companyId,
      legal_entity_ids: legalEntitiesIds,
      import_type: importType,
      tracker_id: tracker.id,
    }),
    [currentUser.id, companyId, legalEntitiesIds, importType, tracker.id]
  )
  const { parsedFile, parseFileLoading, handleFileUpload } = useFileUpload({
    importType,
    onFileUpload: (file) => {
      track(trackingEvents.universalImporter.imports.attachFile, {
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        ...commonEventProperties,
      })
      setOriginalFile(file)
      onUploadedFile?.(file)
    },
  })

  // Auto-trigger file parse when an initialFile is provided (e.g. from the compensation file preview flow).
  // handleFileUpload is intentionally omitted from deps: it is not memoized and would cause
  // infinite re-runs. The ref guard ensures the file is only processed once.
  const initialFileProcessedRef = React.useRef(false)
  const handleFileUploadRef = React.useRef(handleFileUpload)
  handleFileUploadRef.current = handleFileUpload
  useUnsafeEffect(() => {
    if (initialFile && !initialFileProcessedRef.current && !loading) {
      initialFileProcessedRef.current = true
      handleFileUploadRef.current([initialFile])
    }
  }, [initialFile, loading])

  useNuvoSession({
    importType,
    parsedFile,
    initialData,
    loading,
    nuvoImporterRef,
    headerSelection,
    transpose: processBy === 'columns',
    isEdition: !!editionOrFix,
  })
  const { bypassAlertNavigationConfirmation } = useNavigationHandling({
    isDownloadingRef,
    navigate,
    confirmExitMessage: i18n.t('universal_importer.nuvo_importer.confirm_exit'),
    customBackNavigation,
  })
  const { language } = useLanguage({ locale })
  // Combines effectiveIsEdition with fix for UI elements (style, i18nOverrides)
  const effectiveEditionOrFix = effectiveIsEdition || hasFix
  const { columns: rawColumns } = useColumnsMapping({
    data,
    optionMappingConfiguration: {
      layers: optionMappingLayers,
      threshold: thresholdMapping,
      processingMode: 'browser',
    },
    isEdition: !!editionOrFix,
    selectedFields,
  })

  // Find all identifier column keys from schema (fields with identifier: true)
  // This comes from NuvoImporterDocument query, available early before useEditionData
  // For the updater flow, there can be multiple identifier fields (e.g., email + effective_on)
  const identifierColumnKeys = useUnsafeMemo(() => {
    return (
      data?.universalImporter.schemaAdapters
        .filter((item) => item.identifier === true)
        .map((item) => item.key) ?? []
    )
  }, [data?.universalImporter.schemaAdapters])

  // Add mappingValidation to columns based on their required columns validations
  // When useFileUploadInEdition, enforce mapping for ALL identifier columns
  const columns = useUnsafeMemo(() => {
    if (!useFileUploadInEdition) return addMappingValidations(rawColumns)

    if (identifierColumnKeys.length === 0) return rawColumns

    const identifierKeySet = new Set(identifierColumnKeys)
    return rawColumns.map((col) => {
      if (!identifierKeySet.has(col.key)) return col
      return {
        ...col,
        mappingValidation: {
          logic: `mapped('${col.key}')`,
          errorMessage: i18n.t(
            'universal_importer.nuvo_importer.mapping.identifier_error.mapping_required'
          ),
        },
      }
    })
  }, [rawColumns, useFileUploadInEdition, identifierColumnKeys])

  const { columns: customizations } = useColumnCustomizations({ data })

  const { data: importProcessData } = useQuery(UniversalImporterProcessDocument, {
    variables: { importProcessId: String(parentProcessId) },
    skip: !parentProcessId,
  })

  const totalRows = importProcessData?.universalImporter.importProcess?.total ?? 0

  // Build a display label for error messages by joining all identifier column labels
  const identifierColumnLabel = useUnsafeMemo(() => {
    if (identifierColumnKeys.length === 0) return null
    return identifierColumnKeys
      .map((key) => rawColumns.find((col) => col.key === key)?.label ?? key)
      .join(' + ')
  }, [identifierColumnKeys, rawColumns])

  const {
    editionData,
    loading: editionLoading,
    fetchEditionDataForIds,
  } = useEditionData({
    companyId,
    importType,
    metadata: metadataInput,
    // Skip eager DB fetching when useFileUploadInEdition is active (updater flow),
    // unless hasFix is also true (fix flow should still preload its data).
    isEdition: !!editionOrFix && (!useFileUploadInEdition || hasFix),
    selectedFields,
  })

  const handleOnDownload = () => {
    isDownloadingRef.current = true
    onDownload()
    onDownloadTemplate?.()
    track(trackingEvents.universalImporter.imports.downloadTemplate, {
      error: error && value?.toString(),
      ...commonEventProperties,
    })
  }

  // Reset download state when downloading changes
  React.useEffect(() => {
    if (!downloading) {
      isDownloadingRef.current = false
    }
  }, [downloading])

  /**
   * This effect sets initialData when editionData is loaded and loading is complete.
   * This triggers the second useEffect in useNuvoSession to start verification.
   *
   * Skip when useFileUploadInEdition is active (updater flow) — Nuvo should show
   * the uploaded file, not preloaded DB data. Exception: hasFix overrides this
   * because fix data must still be preloaded even if useFileUploadInEdition is set.
   */
  useUnsafeEffect(() => {
    if (!loading && editionOrFix && (!useFileUploadInEdition || hasFix)) {
      setInitialData(editionData)
    }
  }, [editionData, loading, editionOrFix, useFileUploadInEdition, hasFix])

  useUnsafeEffect(() => {
    onLoading?.(loading || editionLoading)
  }, [loading, editionLoading, onLoading])

  if (loading || editionLoading)
    return (
      customLoading ?? (
        <Box justifyContent="center" alignItems="center" height="full">
          <Spinner />
        </Box>
      )
    )

  // Filter columns to only include those that match editionData keys
  const firstEditionRow = editionData[0]

  // Analyze key count for each row to detect structure variations
  const rowKeyCounts = editionData.map((row, index) => ({
    rowIndex: index,
    keyCount: Object.keys(row || {}).length,
    keys: Object.keys(row || {}),
  }))

  const allColumnKeys = new Set(columns.filter(hasKey).map((col) => col.key))
  const firstRowKeys = new Set(Object.keys(firstEditionRow || {}))
  const filteredColumnKeys = new Set([...allColumnKeys].filter((key) => firstRowKeys.has(key)))

  const filteredColumns = firstEditionRow
    ? columns.filter(hasKey).filter((col) => filteredColumnKeys.has(col.key))
    : columns

  const onlyInFirstRow = [...firstRowKeys].filter((key) => !allColumnKeys.has(key))
  const onlyInColumns = [...allColumnKeys].filter((key) => !firstRowKeys.has(key))
  const onlyInFilteredColumns = [...filteredColumnKeys].filter((key) => !firstRowKeys.has(key))
  const hasVariableStructure = new Set(rowKeyCounts.map((r) => r.keyCount)).size > 1

  if (
    (onlyInFirstRow.length > 0 ||
      onlyInFilteredColumns.length > 0 ||
      onlyInColumns.length > 0 ||
      hasVariableStructure) &&
    editionOrFix
  ) {
    const extra = {
      onlyInFirstRow: [...onlyInFirstRow],
      onlyInFilteredColumns: [...onlyInFilteredColumns],
      onlyInColumns: [...onlyInColumns],
      firstRowKeysCount: firstRowKeys.size,
      filteredColumnsKeysCount: filteredColumnKeys.size,
      commonKeysCount: [...firstRowKeys].filter((key) => filteredColumnKeys.has(key)).length,
      firstRowKeys: [...firstRowKeys],
      allColumnKeys: [...allColumnKeys],
      filteredColumnKeys: [...filteredColumnKeys],
      // Row structure analysis
      totalEditionRows: editionData.length,
      keyCountDistribution: rowKeyCounts.reduce<Record<string, number>>((acc, row) => {
        acc[row.keyCount] = (acc[row.keyCount] || 0) + 1
        return acc
      }, {}),
      examplesByKeyCount: Object.fromEntries(
        Array.from(new Set(rowKeyCounts.map((r) => r.keyCount))).map((keyCount) => {
          const example = rowKeyCounts.find((r) => r.keyCount === keyCount)
          return [
            keyCount,
            example
              ? {
                  ...example,
                  keys: [...example.keys], // Expand the keys array for proper Sentry serialization
                }
              : null,
          ]
        })
      ),
      hasVariableStructure: new Set(rowKeyCounts.map((r) => r.keyCount)).size > 1,
      minKeyCount: rowKeyCounts.length > 0 ? Math.min(...rowKeyCounts.map((r) => r.keyCount)) : 0,
      maxKeyCount: rowKeyCounts.length > 0 ? Math.max(...rowKeyCounts.map((r) => r.keyCount)) : 0,
    }

    Sentry.captureMessage(
      `NuvoImporter: Key differences analysis - Company ${companyId}, Type ${importType}`,
      {
        level: 'info',
        tags: {
          component: 'NuvoImporter',
          operation: 'key-comparison',
        },
        extra: extra,
      }
    )
  }

  // Add errors column as disabled if in fix mode
  const columnsWithErrors = hasFix
    ? [
        {
          key: 'errors',
          label: 'Reason',
          columnType: 'string' as const,
          disabled: true,
        },
        ...filteredColumns,
      ]
    : filteredColumns

  return (
    <Box
      gap="s40"
      extra={{
        'data-intercom-target': 'universal-importer-nuvo-importer',
        style: { position: 'relative' },
      }}
    >
      {!editionOrFix && (
        <>
          <Authorized subscription={subscription}>
            <DropzoneBox
              onDrop={(files: File[]) => handleFileUpload(files)}
              iconName="common-file-text-upload"
              message={
                parsedFile?.length && parsedFile[0]?.fileName
                  ? parsedFile[0].fileName
                  : i18n.t('universal_importer.nuvo_importer.upload_file.message')
              }
              description={i18n.t('universal_importer.nuvo_importer.upload_file.description')}
              multiple={false}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                'application/vnd.ms-excel': [],
                'text/csv': [],
                'application/csv': [],
              }}
              maxSize={4}
              disabled={parseFileLoading}
            />
          </Authorized>
          <Box flexDirection="column" alignItems="flexStart">
            <Text.Normal weight="semibold">
              {i18n.t('universal_importer.nuvo_importer.download_template.title')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('universal_importer.nuvo_importer.download_template.description')}
            </Text.Normal>
            <Box paddingTop="s8">
              <F0Button
                onClick={handleOnDownload}
                size="md"
                variant="neutral"
                label={i18n.t('universal_importer.nuvo_importer.download_template.link')}
                loading={downloading}
                icon={Download}
                data-intercom-target="universal-importer-nuvo-importer-download-template"
              />
            </Box>
          </Box>
        </>
      )}
      <Box ref={nuvoImporterRef}>
        <RawNuvoImporter
          licenseKey={nuvoApiKey}
          settings={{
            language,
            developerMode: nuvoDeveloperMode,
            identifier: importType,
            // @ts-expect-error - DropdownOption type compatibility with Nuvo DropdownOptionAPI
            columns: columnsWithErrors,
            onlyMappedColumns: !editionOrFix,
            automaticHeaderDetection: !headerSelection,
            successModalConfiguration: { importSummary: false, display: false },
            submitModalConfiguration: { exportButton: false, importSummary: false, display: true },
            completeImportAction: 'block',
            smartTable: true,
            style: getStyle(
              !!editionOrFix || useFileUploadInEdition,
              useFileUploadInEdition ? filteredRowCount : editionData?.length
            ),
            i18nOverrides: i18nOverrides(!!effectiveEditionOrFix),
            columnMappingConfiguration: {
              layers: columnMappingLayers,
            },
          }}
          stepHandler={{
            headerStep: async () => {
              track(trackingEvents.universalImporter.imports.headerStep, commonEventProperties)
            },
            mappingStep: async ({
              tdm,
              data: mappingData,
              updateData,
              block,
              alert,
            }: {
              tdm: ITdmModifier
              data: Values
              updateData: IMappingStepUpdateData
              block: IBlockFn
              alert: IAlertFn
            }) => {
              track(trackingEvents.universalImporter.imports.mappingStep, commonEventProperties)

              // Extract identifier values from uploaded file and fetch edition data imperatively
              // for useFileUploadInEdition mode (updater flow).
              // Identifier columns are auto-detected from schema (fields with identifier: true).
              // Multiple identifier fields form a composite key (e.g., email + effective_on).
              let fileEditionData: typeof editionData = []
              let matchedIdSet: Set<string> | null = null
              if (useFileUploadInEdition) {
                if (identifierColumnKeys.length === 0) {
                  block({
                    title: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.not_detected.title'
                    ),
                    description: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.not_detected.description'
                    ),
                    closeButton:
                      i18n.t(
                        'universal_importer.nuvo_importer.mapping.identifier_error.close_button'
                      ) || 'Close',
                  })
                  return
                }

                // Build composite keys from all identifier columns for each row
                const allCompositeKeys = mappingData
                  .map((row) => buildCompositeKey(row, identifierColumnKeys))
                  .filter((key) => key !== '')
                const allUniqueKeys = [...new Set(allCompositeKeys)]

                // Scenario 1: No values — identifier columns are empty
                if (allUniqueKeys.length === 0) {
                  const columnName = identifierColumnLabel ?? identifierColumnKeys.join(' + ')
                  block({
                    title: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.no_values.title',
                      { column_name: columnName }
                    ),
                    description: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.no_values.description',
                      { column_name: columnName }
                    ),
                    closeButton:
                      i18n.t(
                        'universal_importer.nuvo_importer.mapping.identifier_error.close_button'
                      ) || 'Close',
                  })
                  return
                }

                // Collect unique values per identifier column for the metadata query.
                // identifierColumnKeys are derived from schema identifier columns (same order as columns). by adding 's' (e.g., email -> emails)
                const identifierEntries = identifierColumnKeys.map((colKey) => {
                  // Find the column definition to check if it's a date column with an outputFormat
                  const col = columns.find((c): c is Column => hasKey(c) && c.key === colKey)
                  const dateOutputFormat =
                    col?.columnType === 'date' && col.outputFormat ? col.outputFormat : null

                  return {
                    key: `${colKey}s`,
                    type: 'string' as const,
                    values: [
                      ...new Set(
                        mappingData
                          .map((row) => {
                            const cell = row[colKey]
                            // Defensively unwrap HookedRecordResult/CurrencyValue objects (same as buildCompositeKey).
                            const raw =
                              typeof cell === 'object' && cell !== null && 'value' in cell
                                ? cell.value
                                : cell
                            const strValue = String(raw ?? '').trim()
                            // Normalize date values from Nuvo's locale-dependent format to ISO YYYY-MM-DD
                            // so the backend can parse them reliably with Date.parse.
                            if (dateOutputFormat && strValue) {
                              return normalizeDateToISO(strValue, dateOutputFormat)
                            }
                            return strValue
                          })
                          .filter((v) => v !== '')
                      ),
                    ],
                  }
                })

                try {
                  fileEditionData = await fetchEditionDataForIds(identifierEntries)
                } catch {
                  block({
                    title: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.fetch_failed.title'
                    ),
                    description: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.fetch_failed.description'
                    ),
                    closeButton:
                      i18n.t(
                        'universal_importer.nuvo_importer.mapping.identifier_error.close_button'
                      ) || 'Close',
                  })
                  return
                }

                // Build composite keys from returned edition data and find matches
                const persistedKeys = new Set(
                  fileEditionData
                    .map((row) => buildCompositeKey(row, identifierColumnKeys))
                    .filter((key) => key !== '')
                )
                const matchedKeys = allUniqueKeys.filter((key) => persistedKeys.has(key))
                const unmatchedKeys = allUniqueKeys.filter((key) => !persistedKeys.has(key))
                matchedIdSet = new Set(matchedKeys)

                // Scenario 2: No rows match existing records in the system
                if (matchedKeys.length === 0) {
                  block({
                    title: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.no_matches.title'
                    ),
                    description: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_error.no_matches.description'
                    ),
                    closeButton:
                      i18n.t(
                        'universal_importer.nuvo_importer.mapping.identifier_error.close_button'
                      ) || 'Close',
                  })
                  return
                }

                // Scenario 3: Some records matched but others will be skipped
                if (unmatchedKeys.length > 0) {
                  const columnName = identifierColumnLabel ?? identifierColumnKeys.join(' + ')
                  const description =
                    unmatchedKeys.length <= 10
                      ? i18n.t(
                          'universal_importer.nuvo_importer.mapping.identifier_mismatch.description',
                          {
                            matched: matchedKeys.length,
                            total: allUniqueKeys.length,
                            column_name: columnName,
                            skipped_list: unmatchedKeys.join(', '),
                          }
                        )
                      : i18n.t(
                          'universal_importer.nuvo_importer.mapping.identifier_mismatch.description_percentage',
                          {
                            matched: matchedKeys.length,
                            total: allUniqueKeys.length,
                            skipped: Math.round(
                              (unmatchedKeys.length / allUniqueKeys.length) * 100
                            ),
                          }
                        )

                  alert({
                    title: i18n.t(
                      'universal_importer.nuvo_importer.mapping.identifier_mismatch.title'
                    ),
                    description,
                    cancelButton: i18n.t('common.cancel'),
                    proceedButton: i18n.t('common.continue'),
                  })
                }
              }

              // When useFileUploadInEdition, add conditionally-required columns (required_with / required_with_all)
              // that the user didn't map, and build an index of fileEditionData by composite identifier for filling those columns
              let missingColumnKeys: string[] = []
              let missingColumnType = new Map<string, string>()
              let editionDataByIdentifier = new Map<string, EditionData[number]>()
              if (
                useFileUploadInEdition &&
                fileEditionData.length > 0 &&
                identifierColumnKeys.length > 0
              ) {
                const mappedKeys = new Set(Object.keys(mappingData[0] || {}))
                ;({ missingColumnKeys, missingColumnType, editionDataByIdentifier } =
                  addMissingConditionalColumns(
                    tdm,
                    rawColumns,
                    mappedKeys,
                    fileEditionData,
                    identifierColumnKeys
                  ))
                editionDataByIdentifierRef.current = editionDataByIdentifier
              }

              // Add ALL hidden columns dynamically. They are filtered out of
              // useColumnsMapping so they never appear in the Match Entries view,
              // but Nuvo still needs them for transformations, conditional
              // rendering, and internal values.
              const hiddenTransformationColumns: string[] = []
              const hiddenDataColumns: string[] = []
              const transformationKeys = new Set(
                customizations
                  .filter(
                    (c: ColumnCustomization) =>
                      c.hidden && c.customizations?.some((cx) => cx.type === 'transformation')
                  )
                  .map((c: ColumnCustomization) => c.key)
              )

              data?.universalImporter.schemaAdapters
                .filter((node) => node.hidden)
                .forEach((node) => {
                  // Find full customization info if available (for validations, disabled, etc.)
                  const cust = customizations.find((c: ColumnCustomization) => c.key === node.key)
                  tdm.addColumn({
                    key: node.key,
                    label: node.label,
                    columnType: node.columnType,
                    hidden: node.disabled ? false : true,
                    disabled: node.disabled,
                    validations: cust?.validations || undefined,
                    // @ts-expect-error - DropdownOption type enum compatibility with Nuvo DropdownOptionAPI
                    dropdownOptions:
                      node.dropdownOptions?.map(
                        ({ __typename: _, validations: __, ...opt }) => opt
                      ) ?? undefined,
                  })
                  if (transformationKeys.has(node.key)) {
                    hiddenTransformationColumns.push(node.key)
                  } else {
                    hiddenDataColumns.push(node.key)
                  }
                })

              // Initialize data with hidden/missing columns and apply customizations
              const processedData = mappingData.map((row, rowIndex) => {
                const newRow: Record<string, FieldValue> = {}

                // Copy existing row data
                Object.keys(row).forEach((key) => {
                  newRow[key] = row[key]
                })

                // Fill missing required columns with values from fileEditionData
                if (missingColumnKeys.length > 0 && identifierColumnKeys.length > 0) {
                  const compositeKey = buildCompositeKey(newRow, identifierColumnKeys)
                  const editionRow = editionDataByIdentifier.get(compositeKey)
                  missingColumnKeys.forEach((colKey) => {
                    newRow[colKey] = convertEditionValue(
                      editionRow?.[colKey],
                      missingColumnType.get(colKey)
                    )
                  })
                }

                // Initialize hidden transformation columns with null (transformations will compute values)
                hiddenTransformationColumns.forEach((colName) => {
                  newRow[colName] = null
                })

                // Fill hidden data columns (no transformation) from DB edition data.
                // In useFileUploadInEdition mode: look up by composite identifier from fileEditionData.
                // In normal edition mode: use initialData by row index.
                if (hiddenDataColumns.length > 0) {
                  const editionSource =
                    useFileUploadInEdition && identifierColumnKeys.length > 0
                      ? editionDataByIdentifier.get(buildCompositeKey(newRow, identifierColumnKeys))
                      : initialData?.[rowIndex]

                  hiddenDataColumns.forEach((colKey) => {
                    const val = editionSource?.[colKey]
                    if (typeof val === 'string') {
                      newRow[colKey] = val
                    } else if (Array.isArray(val)) {
                      newRow[colKey] = val
                    } else {
                      newRow[colKey] = null
                    }
                  })
                }

                // Apply transformations and validations
                if (customizations.length > 0) {
                  return applyCustomizationsToRow(
                    newRow,
                    customizations,
                    transformationFunctions,
                    validationFunctions,
                    metadata,
                    columns
                  )
                }

                return newRow
              })

              // When useFileUploadInEdition: filter unmatched rows and always call updateData
              // (missing required columns were added and need to be persisted)
              // Otherwise: only call updateData if transformations modified the data
              if (useFileUploadInEdition && matchedIdSet && identifierColumnKeys.length > 0) {
                const skipKeys = new Set([...missingColumnKeys, ...hiddenTransformationColumns])
                const changeMessage = i18n.t(
                  'universal_importer.nuvo_importer.mapping.previous_value'
                )
                const dropdownMaps = buildDropdownMaps(rawColumns)
                const multiSelectKeys = new Set(
                  rawColumns.filter((col) => col.isMultiSelect).map((col) => col.key)
                )
                const filteredData = processedData
                  .filter((row) => {
                    const compositeKey = buildCompositeKey(row, identifierColumnKeys)
                    return compositeKey !== '' && matchedIdSet.has(compositeKey)
                  })
                  .map((row) => {
                    const compositeKey = buildCompositeKey(row, identifierColumnKeys)
                    const editionRow = editionDataByIdentifier.get(compositeKey)
                    if (!editionRow) return row
                    return annotateChangedColumns(
                      row,
                      editionRow,
                      identifierColumnKeys,
                      skipKeys,
                      changeMessage,
                      dropdownMaps,
                      multiSelectKeys
                    )
                  })
                setFilteredRowCount(filteredData.length)
                updateData(filteredData)
              } else if (hiddenDataColumns.length > 0 || customizations.length > 0) {
                updateData(processedData)
              }
            },
            reviewStep: async ({
              data: reviewStepData,
              updateData,
            }: {
              data: IReviewData
              updateData: (data: IReviewEntriesUpdatedData) => void
              block: IBlockFn
              logs: ImportLogs
            }): Promise<void> => {
              track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)

              // Always extract values from IReviewData format and apply customizations
              const newData: IReviewEntriesUpdatedData = reviewStepData.map((row, rowIndex) => {
                // Copy existing values, extracting the actual value from info object if present
                const extractedRow: Record<string, FieldValue | HookedRecordResult> = {}
                Object.keys(row).forEach((key) => {
                  extractedRow[key] = row[key]?.value ?? row[key]
                })

                // Look up the original DB row so the policy validator can
                // distinguish a real edit from a no-op (label vs ID, sort order,
                // whitespace, etc.) and avoid false-positive policy_denied errors.
                const editionRow =
                  useFileUploadInEdition && identifierColumnKeys.length > 0
                    ? editionDataByIdentifierRef.current.get(
                        buildCompositeKey(extractedRow, identifierColumnKeys)
                      )
                    : initialData?.[rowIndex]

                // Apply transformations and validations if there are customizations
                if (customizations.length > 0) {
                  return applyCustomizationsToRow(
                    extractedRow,
                    customizations,
                    transformationFunctions,
                    validationFunctions,
                    metadata,
                    columns,
                    { editionRow }
                  )
                }

                return extractedRow
              })

              updateData(newData)
            },
          }}
          onCancel={() => {
            track(trackingEvents.universalImporter.imports.cancelled, commonEventProperties)
            tracker.refresh()
            bypassAlertNavigationConfirmation()
            if (customBackNavigation) {
              navigate(customBackNavigation)
            } else {
              navigate(-1)
            }
          }}
          onEntryChange={(rows, _logs) => {
            if (isEdition && !hasFix && !hasChanges) {
              setHasChanges(true)
            }

            const shouldRecalculateAnnotations = useFileUploadInEdition || isEdition

            // Early return if no transformations and no edition annotations to recalculate
            if (customizations.length === 0 && !shouldRecalculateAnnotations) {
              return
            }

            // Build dropdown maps once for annotation recalculation in edition flows
            const changedCellMaps = shouldRecalculateAnnotations
              ? buildDropdownMaps(rawColumns)
              : null
            const multiSelectKeys = shouldRecalculateAnnotations
              ? new Set(rawColumns.filter((col) => col.isMultiSelect).map((col) => col.key))
              : null
            const changeMessage = i18n.t('universal_importer.nuvo_importer.mapping.previous_value')

            // Transform rows to correct EntryChangeResult[] format
            const entryChangeResults: EntryChangeResult[] = rows.map((rowData) => {
              const { data: row, actionType, rowIndex, changeLog, info: rowInfo } = rowData

              if (actionType === 'delete') {
                return {
                  rowIndex: rowIndex,
                  data: { ...row },
                  actionType,
                }
              }

              // Look up the original DB row for this entry (updater and edition flow only)
              const editionRow =
                useFileUploadInEdition && identifierColumnKeys.length > 0
                  ? editionDataByIdentifierRef.current.get(
                      buildCompositeKey(row, identifierColumnKeys)
                    )
                  : initialData?.[rowIndex]

              const processedRow =
                customizations.length > 0
                  ? applyCustomizationsToRow(
                      { ...row },
                      customizations,
                      transformationFunctions,
                      validationFunctions,
                      metadata,
                      columns,
                      {
                        wrapWithRowResult: true,
                        editionRow,
                      }
                    )
                  : Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v]))

              const changedKeys = changeLog ? new Set(Object.keys(changeLog)) : new Set<string>()

              for (const key of Object.keys(processedRow)) {
                const isChangedCell = changedKeys.has(key)

                // For cells the user just edited in edition flows: recalculate
                // the annotation by comparing the new value with the original
                // DB value instead of blindly restoring the previous annotation
                if (isChangedCell && editionRow && changedCellMaps && multiSelectKeys) {
                  const current = processedRow[key]
                  const rawValue = isHookedRecordResult(current) ? (current.value ?? null) : current
                  const annotated = computeCellAnnotation(
                    isHookedRecordResult(current)
                      ? { ...current, info: current.info?.filter((i) => i.level === 'error') ?? [] }
                      : rawValue,
                    editionRow[key],
                    key,
                    changeMessage,
                    changedCellMaps.labelToValue,
                    changedCellMaps.valueToLabel,
                    multiSelectKeys
                  )
                  processedRow[key] = annotated
                  continue
                }

                // For non-changed cells: restore previous info/warning annotations
                const infoAnnotations =
                  rowInfo?.[key]?.filter((i) => i.level === 'info' || i.level === 'warning') ?? []
                if (infoAnnotations.length === 0) continue

                const current = processedRow[key]
                if (isHookedRecordResult(current)) {
                  processedRow[key] = {
                    ...current,
                    info: [...infoAnnotations, ...(current.info ?? [])],
                  }
                } else if (isFieldValue(current)) {
                  processedRow[key] = { value: current ?? null, info: infoAnnotations }
                }
              }

              return {
                rowIndex: rowIndex,
                data: processedRow,
                actionType,
              }
            })

            return entryChangeResults
          }}
          onResults={async (result, errors, complete, logs, block) => {
            if (!result.length) {
              block({
                title: i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.title'),
                description: i18n.t(
                  'universal_importer.nuvo_importer.results.no_rows_alert.description'
                ),
                closeButton:
                  i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.close_button') ||
                  'Close',
              })
              return
            }

            track(trackingEvents.universalImporter.imports.processRequested, {
              total_rows: result.length,
              rows_with_errors: errors.length,
              ...commonEventProperties,
            })

            if (isEdition && !hasFix && !hasChanges) {
              block({
                title: i18n.t('universal_importer.nuvo_importer.results.no_changes_alert.title'),
                description: i18n.t(
                  'universal_importer.nuvo_importer.results.no_changes_alert.description'
                ),
                closeButton:
                  i18n.t(
                    'universal_importer.nuvo_importer.results.no_changes_alert.close_button'
                  ) || 'Close',
              })
              return
            }

            const response = await sendImportResults({
              data: result,
              metadata: metadataInput,
              companyId,
              importType,
              parentProcessId,
              rootProcessId,
              isEdition: effectiveIsEdition,
              originalFile,
            })

            if (response.success) {
              const columnNames = Object.keys(result?.[0] || {})

              const columnPercentages = columnNames
                .map((columnName) => {
                  const columnValues = result.map((row) => row[columnName])
                  const percentage =
                    (columnValues.filter(Boolean).length / columnValues.length) * 100
                  return { [columnName]: percentage.toFixed(2) }
                })
                .reduce((acc, curr) => ({ ...acc, ...curr }), {})
              if (hasFix) {
                track(trackingEvents.universalImporter.imports.startedFixErrors, {
                  total_rows: totalRows,
                  ...commonEventProperties,
                  import_process_id: response.value.id,
                  total_failed_rows: result.length,
                })
              } else {
                track(trackingEvents.universalImporter.imports.started, {
                  total_rows: result.length,
                  ...commonEventProperties,
                  import_process_id: response.value.id,
                  column_percentages: JSON.stringify(columnPercentages),
                })
              }

              complete()
              bypassAlertNavigationConfirmation()
              onCompleted?.()
              if (customFinishNavigation) {
                navigate(customFinishNavigation)
              } else {
                navigate(-1)
              }
            } else {
              const errorMessage = response.value.message || ''
              track(trackingEvents.universalImporter.imports.processRequestError, {
                total_rows: result.length,
                error_message:
                  errorMessage.length > 500 ? `${errorMessage.slice(0, 500)}...` : errorMessage,
                ...commonEventProperties,
              })

              onError?.(response.value.message)
              block({
                title: i18n.t('universal_importer.nuvo_importer.results.api_response_alert.title'),
                description: i18n.t(
                  'universal_importer.nuvo_importer.results.api_response_alert.description'
                ),
                closeButton:
                  i18n.t(
                    'universal_importer.nuvo_importer.results.api_response_alert.close_button'
                  ) || 'Close',
              })
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default NuvoImporter
